// /l/:slug — what anyone with a share link sees: the list, its owner, the
// owner's progress and per-title coin tiers. Public, no auth required.
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { EmptyShareState, PublicPageCTA, PublicShareLayout } from '../../components/PublicShare/PublicShareLayout';
import ShareMenu from '../../components/PublicShare/ShareMenu';
import { MyListsService } from '../../services/myListsService';
import { useAuth } from '../../context/AuthContext';
import { useShareMetadata } from '../../utils/shareMetadata';
import { TIER_COLORS } from '../../utils/mediaProgression';
import { getListItemHref } from '../../utils/myListItems';
import { ANALYTICS_EVENTS, trackEvent } from '../../analytics';
import styles from './PublicListShare.module.css';

const TIER_LABELS = { bronze: 'Bronze', silver: 'Silver', gold: 'Gold', legendary: 'Legendary' };

export function SharedListView({ payload, viewerId }) {
    const { list, owner, items } = payload;
    const ownerName = owner.displayName || `@${owner.username}`;
    const pct = list.itemCount ? Math.round((list.verifiedCount / list.itemCount) * 100) : 0;
    const isOwner = viewerId && viewerId === owner.id;

    return (
        <PublicShareLayout>
            <header className={styles.hero}>
                <span className={styles.eyebrow}>A LIST BY <Link to={`/u/${owner.username}`}>{ownerName}</Link></span>
                <h1>{list.name}</h1>
                {list.description && <p className={styles.description}>{list.description}</p>}
                <p className={styles.meta}>{list.itemCount} {list.itemCount === 1 ? 'title' : 'titles'} · {list.verifiedCount} verified</p>
                <div className={styles.progress} role="group" aria-label="Owner's progress">
                    <span>{list.verifiedCount} of {list.itemCount} verified</span>
                    <div className={styles.track} aria-hidden="true"><div className={styles.fill} style={{ width: `${pct}%` }} /></div>
                </div>
                <div className={styles.actions}>
                    <ShareMenu url={payload.shareUrl} title={`${list.name} on Aluvo`} text={`${list.name} — a list by ${ownerName} on Aluvo 🎬`} compact />
                    {isOwner && <Link to={`/lists/${list.id}`} className={styles.manage}>Manage list</Link>}
                </div>
            </header>

            {items.length ? (
                <section className={styles.grid} aria-label="List titles">
                    {items.map((item) => {
                        const tierColor = item.ownerTier ? TIER_COLORS[item.ownerTier] : null;
                        return (
                            <article key={item.id} className={styles.card}>
                                <Link to={getListItemHref({ mediaType: item.mediaType ?? item.kind, mediaId: item.mediaId ?? item.tmdbId, seasonNumber: item.seasonNumber })} className={styles.posterWrap}>
                                    {item.posterUrl
                                        ? <img src={item.posterUrl} alt={`${item.displayTitle} poster`} loading="lazy" />
                                        : <div className={styles.placeholder}>{item.kind === 'tv' ? '📺' : '🎬'}</div>}
                                    {tierColor && (
                                        <span className={styles.tier} style={{ borderColor: tierColor, color: tierColor }}>
                                            {TIER_LABELS[item.ownerTier]}{item.ownerTier === 'legendary' ? ' ✓' : ''}
                                        </span>
                                    )}
                                </Link>
                                <h2>{item.displayTitle}</h2>
                                <p>{[item.kind === 'tv' ? 'TV' : 'Movie', item.year].filter(Boolean).join(' · ')}</p>
                            </article>
                        );
                    })}
                </section>
            ) : (
                <p className={styles.empty}>This list is empty so far.</p>
            )}

            {!isOwner && (
                <PublicPageCTA
                    title="Make your own lists"
                    text="Save what you want to watch, verify each title with a quick challenge, and share your progress."
                    buttonText="Join Aluvo"
                />
            )}
        </PublicShareLayout>
    );
}

export default function PublicListShare() {
    const { slug } = useParams();
    const { user } = useAuth();
    const [payload, setPayload] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        let mounted = true;
        MyListsService.getShared(slug)
            .then((data) => {
                if (!mounted) return;
                setPayload(data);
                if (!data.isPrivate) trackEvent(ANALYTICS_EVENTS.SHARED_LIST_VIEWED, { list_item_count: data.list?.itemCount ?? 0 }, { onceKey: `list:${slug}` });
            })
            .catch((err) => mounted && setError(err.message || 'List not found'));
        return () => { mounted = false; };
    }, [slug]);

    const metadata = useMemo(() => {
        if (!payload || payload.isPrivate) return { title: 'Aluvo list', description: 'A list on Aluvo.', url: `${window.location.origin}/l/${slug}` };
        const ownerName = payload.owner.displayName || `@${payload.owner.username}`;
        return {
            title: `${payload.list.name} | Aluvo`,
            description: payload.list.description || `${payload.list.itemCount} titles picked by ${ownerName} — ${payload.list.verifiedCount} verified on Aluvo.`,
            url: payload.shareUrl || `${window.location.origin}/l/${slug}`,
            image: payload.cardUrl,
            type: 'article',
        };
    }, [payload, slug]);
    useShareMetadata(metadata);

    if (error) return <EmptyShareState icon="🎬" title="List not found" message="This list does not exist or is no longer shared." />;
    if (!payload) return <EmptyShareState icon="◎" title="Loading list" message="Gathering movies and shows..." />;
    if (payload.isPrivate) return <EmptyShareState icon="🔒" title="This list is private." message="The owner has turned off sharing for this list." />;

    return <SharedListView payload={payload} viewerId={user?.id} />;
}
